  if (!customElements.get('gift-finder')) {
    const connectedGiftFinders = [];

    class HeartfeltGiftFinder extends HTMLElement {
      connectedCallback() {
        const wasInitialised = Boolean(this.hfGiftFinderInitialised);
        const currentHash = this.currentLocationHash();
        const shouldHandleHash = !wasInitialised || currentHash !== this.lastObservedHash;
        this.hfGiftFinderConnected = true;
        this.registerHashOwner();
        this.installWindowLifecycle();
        this.lastObservedHash = currentHash;
        const hashFocused = shouldHandleHash ? this.focusForOwnHash() : false;
        if (wasInitialised) {
          if (this.remoteMode && !this.routesLoaded) this.scheduleRouteLoad();
          this.beginInitialUrlRestore({ completionFocus: hashFocused, force: true });
          return;
        }
        this.hfGiftFinderInitialised = true;
        this.filters = ['recipient', 'occasion', 'giftType', 'budget'];
        this.remoteMode = Boolean(this.dataset.sourceUrl && this.dataset.resultsSectionId);
        this.autoAdvanceChoices = this.dataset.autoAdvance === 'true';
        this.grid = this.querySelector('[data-gf-grid]');
        this.routesLoaded = !this.remoteMode;
        this.routesLoadFailed = false;
        this.activateReviewedRenderedSourceScope();
        this.hfStaticRouteCountsTrusted = false;
        this.routePrefixCounts = {};
        this.routeMinimums = {};
        this.remotePage = 1;
        this.remoteHasNext = false;
        this.remoteTotal = 0;
        this.remoteLoading = false;
        this.remoteError = false;
        this.remoteRequestId = 0;
        this.routesLoadPromise = null;
        this.routeObserver = null;
        this.routeAbortController = null;
        this.remoteAbortController = null;
        this.pendingAdvanceFilter = '';
        this.pendingAdvanceId = 0;
        this.urlRestoreId = 0;
        this.completedUrlRestoreId = 0;
        this.pendingRouteUrlRestore = null;
        this.initialUrlRestoreComplete = false;
        this.initialUrlRestorePromise = null;
        this.pendingCompletionFocusId = 0;
        this.pendingCompletionFocus = null;
        this.pendingLoadMoreFocusId = 0;
        this.pendingLoadMoreFocus = null;
        this.routeLoading = false;
        this.cards = Array.from(this.querySelectorAll('.hf-gf__card'));
        this.records = this.remoteMode ? [] : this.cards.map((card, index) => this.createRecord(card, index));
        this.empty = this.querySelector('.hf-gf__empty');
        this.emptyDefaultText = this.empty ? this.empty.textContent : '';
        this.selects = Array.from(this.querySelectorAll('select[data-filter]'));
        this.selects.forEach((select) => {
          if (select.dataset.answered !== 'true') select.value = '';
        });
        this.ensureExtraTypes();
        this.options = Array.from(this.querySelectorAll('[data-gf-option]'));
        this.panels = Array.from(this.querySelectorAll('[data-gf-panel]'));
        this.steps = Array.from(this.querySelectorAll('.hf-gf__step'));
        this.stepButtons = Array.from(this.querySelectorAll('[data-gf-step]'));
        this.count = this.querySelector('[data-gf-count]');
        this.countLabel = this.querySelector('[data-gf-count-label]');
        this.resultsFocus = this.querySelector('[data-gf-results-focus]');
        this.liveStatus = this.querySelector('[data-gf-live-status]');
        this.optionNote = this.querySelector('[data-gf-option-note]');
        this.routeStatus = this.querySelector('[data-gf-route-status]');
        this.reset = this.querySelector('[data-gf-reset]');
        this.back = this.querySelector('[data-gf-back]');
        this.next = this.querySelector('[data-gf-next]');
        this.loadMore = this.querySelector('[data-gf-load-more]');
        this.selected = this.querySelector('[data-gf-selected]');
        this.question = this.querySelector('.hf-gf__question');
        this.panelTitle = this.querySelector('.hf-gf__panel-title');
        this.panelText = this.querySelector('.hf-gf__panel-text');
        this.activeFilter = this.filters[0];
        this.lastSignature = '';
        this.visibleLimit = this.initialLimit();
        if (this.remoteMode) this.scheduleRouteLoad();
        if (this.question && !this.question.dataset.initialText) this.question.dataset.initialText = this.question.textContent;
        this.selects.forEach((s) => s.addEventListener('change', (event) => {
          this.cancelUrlRestore();
          this.cancelPendingInteractionFocus();
          s.dataset.answered = 'true';
          s.dataset.selectedLabel = s.options[s.selectedIndex] ? s.options[s.selectedIndex].text : '';
          const index = this.filters.indexOf(s.dataset.filter);
          this.clearSelectionsAfter(index);
          this.resetLimit();
          if (this.autoAdvanceChoices) {
            this.advanceFromFilter(s.dataset.filter, {
              focusOwner: this.interactionFocusOwner(event, s)
            });
          } else {
            this.activateFilter(s.dataset.filter, { focus: false });
          }
        }));
        this.options.forEach((option) => option.addEventListener('click', (event) => {
          if (option.disabled || option.hidden) return;
          const select = this.getSelect(option.dataset.filter);
          if (!select) return;
          const wasAnswered = this.isAnswered(option.dataset.filter);
          const previousValue = this.canonicalValue(option.dataset.filter, select.value);
          select.value = option.dataset.value || '';
          select.dataset.answered = 'true';
          select.dataset.selectedLabel = option.textContent.trim();
          const index = this.filters.indexOf(option.dataset.filter);
          this.cancelUrlRestore();
          this.cancelPendingInteractionFocus();
          if (!wasAnswered || previousValue !== this.canonicalValue(option.dataset.filter, select.value)) {
            this.clearSelectionsAfter(index);
          }
          this.resetLimit();
          if (this.autoAdvanceChoices) {
            this.advanceFromFilter(option.dataset.filter, {
              focusOwner: this.interactionFocusOwner(event, option)
            });
          } else {
            this.activateFilter(option.dataset.filter, { focus: false });
          }
        }));
        this.stepButtons.forEach((button) => button.addEventListener('click', () => {
          const index = Number(button.dataset.stepIndex);
          if (!this.canAccessStep(index)) return;
          this.cancelUrlRestore();
          this.cancelPendingInteractionFocus();
          this.activateFilter(this.filters[index], { focus: true });
        }));
        if (this.reset) {
          this.reset.addEventListener('click', () => {
            this.cancelUrlRestore();
            this.cancelPendingInteractionFocus();
            this.pendingAdvanceId += 1;
            this.pendingAdvanceFilter = '';
            this.setRouteLoading(false);
            this.selects.forEach((s) => {
              this.clearSelection(s.dataset.filter);
            });
            this.resetLimit();
            this.announceStatus('');
            this.activateFilter(this.filters[0], { focus: true });
          });
        }
        if (this.back) {
          this.back.addEventListener('click', () => {
            const index = this.activeFilter ? this.filters.indexOf(this.activeFilter) : this.filters.length;
            if (index <= 0) return;
            this.cancelUrlRestore();
            this.cancelPendingInteractionFocus();
            this.activateFilter(this.filters[index - 1], { focus: true });
          });
        }
        if (this.next) {
          this.next.addEventListener('click', (event) => {
            this.cancelPendingInteractionFocus();
            if (this.remoteMode && this.routesLoadFailed) {
              this.retryRoutes();
              return;
            }
            this.cancelUrlRestore();
            if (!this.activeFilter) {
              if (this.interactionFocusOwner(event, this.next)) this.focusResults();
              return;
            }
            this.advanceFromFilter(this.activeFilter, {
              focusOwner: this.interactionFocusOwner(event, this.next)
            });
          });
        }
        if (this.loadMore) {
          this.loadMore.addEventListener('click', (event) => {
            if (this.remoteMode) {
              const values = this.currentSelection();
              const hiddenLocalResults = this.remoteItems().some((item) => this.remoteItemMatches(item, values) && item.hidden);
              if (hiddenLocalResults) {
                const focusTransaction = this.beginLoadMoreFocusTransfer(event);
                this.visibleLimit += this.loadIncrement();
                const display = this.applyRemoteDisplay(values);
                this.announceResultProgress(display.visible, display.total);
                this.settleLoadMoreFocus(focusTransaction);
                return;
              }
              if (this.remoteLoading || !this.remoteHasNext) return;
              const focusTransaction = this.beginLoadMoreFocusTransfer(event);
              this.visibleLimit += this.loadIncrement();
              this.fetchRemoteResults(this.currentSelection(), false, focusTransaction);
              return;
            }
            const focusTransaction = this.beginLoadMoreFocusTransfer(event);
            this.visibleLimit += this.loadIncrement();
            this.apply();
            this.settleLoadMoreFocus(focusTransaction);
          });
        }
        this.apply();
        this.beginInitialUrlRestore({ completionFocus: hashFocused });
      }
      disconnectedCallback() {
        this.hfGiftFinderConnected = false;
        this.unregisterHashOwner();
        this.removeWindowLifecycle();
        this.cancelScheduledRouteLoad();
        this.pendingAdvanceId += 1;
        this.pendingAdvanceFilter = '';
        this.urlRestoreId += 1;
        this.pendingRouteUrlRestore = null;
        this.initialUrlRestoreComplete = false;
        this.initialUrlRestorePromise = null;
        this.cancelPendingInteractionFocus();
        this.routeAbortController?.abort();
        this.routeAbortController = null;
        this.routesLoadPromise = null;
        this.remoteRequestId = Number(this.remoteRequestId || 0) + 1;
        this.remoteAbortController?.abort();
        this.remoteAbortController = null;
        this.setLoading(false);
        this.setRouteLoading(false);
      }
      installWindowLifecycle() {
        if (this.windowLifecycleAttached || typeof window === 'undefined') return;
        this.boundHashChange = this.boundHashChange || (() => {
          this.lastObservedHash = this.currentLocationHash();
          this.focusForOwnHash();
        });
        this.boundPopState = this.boundPopState || (() => {
          this.lastObservedHash = this.currentLocationHash();
          this.restoreUrlState({
            focus: this.ownHashTargeted(),
            fromPopstate: true
          });
        });
        window.addEventListener('hashchange', this.boundHashChange);
        window.addEventListener('popstate', this.boundPopState);
        this.windowLifecycleAttached = true;
      }
      removeWindowLifecycle() {
        if (!this.windowLifecycleAttached || typeof window === 'undefined') return;
        window.removeEventListener('hashchange', this.boundHashChange);
        window.removeEventListener('popstate', this.boundPopState);
        this.windowLifecycleAttached = false;
      }
      registerHashOwner() {
        if (!connectedGiftFinders.includes(this)) connectedGiftFinders.push(this);
      }
      unregisterHashOwner() {
        const index = connectedGiftFinders.indexOf(this);
        if (index >= 0) connectedGiftFinders.splice(index, 1);
      }
      currentLocationHash() {
        return typeof window === 'undefined' ? '' : String(window.location?.hash || '');
      }
      ownHashTargeted() {
        if (!this.id || typeof window === 'undefined') return false;
        const hash = String(window.location?.hash || '').slice(1);
        try {
          const decodedHash = decodeURIComponent(hash);
          if (decodedHash === this.id) return true;
          const anchorId = String(this.dataset.anchorId || '');
          if (!anchorId || decodedHash !== anchorId) return false;
          return connectedGiftFinders.find(
            (finder) => finder.hfGiftFinderConnected !== false
              && String(finder.dataset.anchorId || '') === anchorId
          ) === this;
        } catch (_error) {
          return false;
        }
      }
      focusForOwnHash() {
        if (!this.ownHashTargeted()) return false;
        this.focus({ preventScroll: true });
        if (document.activeElement !== this) return false;
        this.scrollIntoView({ block: 'start', behavior: this.scrollBehavior() });
        return true;
      }
      urlKeyForFilter(filter) {
        return {
          recipient: 'gf_recipient',
          occasion: 'gf_occasion',
          giftType: 'gf_gift_type',
          budget: 'gf_budget'
        }[filter] || '';
      }
      urlStepForFilter(filter) {
        return filter === 'giftType' ? 'gift_type' : filter;
      }
      filterForUrlStep(step) {
        return step === 'gift_type' ? 'giftType' : this.filters.includes(step) ? step : '';
      }
      giftFinderUrlKeys() {
        return ['gf_recipient', 'gf_occasion', 'gf_gift_type', 'gf_budget', 'gf_step'];
      }
      hasGiftFinderUrlState() {
        if (typeof window === 'undefined') return false;
        const params = new URL(window.location.href).searchParams;
        return [...params.keys()].some((key) => key.startsWith('gf_'));
      }
      clearSelectionsAfter(index) {
        this.filters.forEach((filter, filterIndex) => {
          if (filterIndex > index) this.clearSelection(filter);
        });
      }
      optionForUrlValue(filter, rawValue) {
        const select = this.getSelect(filter);
        if (!select) return null;
        if (rawValue === 'any') {
          return Array.from(select.options).find((option) => this.canonicalValue(filter, option.value) === '') || null;
        }
        const normalized = this.canonicalValue(filter, rawValue);
        if (!normalized) return null;
        return Array.from(select.options).find(
          (option) => this.canonicalValue(filter, option.value) === normalized
        ) || null;
      }
      setSelectionFromOption(filter, option) {
        const select = this.getSelect(filter);
        if (!select || !option) return false;
        select.value = option.value || '';
        select.dataset.answered = 'true';
        select.dataset.selectedLabel = option.text || option.textContent || '';
        return true;
      }
      currentUrlState() {
        const state = {};
        let prefixOpen = true;
        this.filters.forEach((filter) => {
          if (!prefixOpen || !this.isAnswered(filter)) {
            prefixOpen = false;
            return;
          }
          const value = this.canonicalValue(filter, this.getSelect(filter)?.value || '');
          state[this.urlKeyForFilter(filter)] = value || 'any';
        });
        state.gf_step = this.activeFilter ? this.urlStepForFilter(this.activeFilter) : 'results';
        return state;
      }
      urlHasCanonicalGiftFinderState(url, state) {
        const allowed = new Set(this.giftFinderUrlKeys());
        const keys = [...url.searchParams.keys()];
        if (keys.some((key) => key.startsWith('gf_') && !allowed.has(key))) return false;
        return [...allowed].every((key) => {
          const expected = Object.hasOwn(state, key) ? String(state[key]) : null;
          const actual = url.searchParams.getAll(key);
          return expected === null ? actual.length === 0 : actual.length === 1 && actual[0] === expected;
        });
      }
      replaceGiftFinderUrlState(state) {
        if (typeof window === 'undefined') return false;
        const url = new URL(window.location.href);
        if (this.urlHasCanonicalGiftFinderState(url, state)) return false;
        [...new Set(url.searchParams.keys())].forEach((key) => {
          if (key.startsWith('gf_')) url.searchParams.delete(key);
        });
        this.giftFinderUrlKeys().forEach((key) => {
          if (Object.hasOwn(state, key)) url.searchParams.set(key, state[key]);
        });
        window.history.replaceState(window.history.state, '', url.toString());
        return true;
      }
      serializeUrlState() {
        if (this.restoringUrlState) return false;
        return this.replaceGiftFinderUrlState(this.currentUrlState());
      }
      cancelUrlRestore() {
        this.urlRestoreId = Number(this.urlRestoreId || 0) + 1;
        this.pendingRouteUrlRestore = null;
        this.initialUrlRestoreComplete = true;
        this.initialUrlRestorePromise = null;
      }
      beginInitialUrlRestore({ completionFocus = false, force = false } = {}) {
        if (this.initialUrlRestoreComplete || this.initialUrlRestorePromise) return;
        if (!this.hasGiftFinderUrlState()) {
          if (force && Array.isArray(this.filters)) {
            this.filters.forEach((filter) => this.clearSelection(filter));
            this.activeFilter = this.filters[0] || null;
            this.resetLimit();
            this.apply();
          }
          this.initialUrlRestoreComplete = true;
          return;
        }
        const promise = this.restoreUrlState({ completionFocus, focus: false, fromPopstate: false });
        this.initialUrlRestorePromise = promise;
        Promise.resolve(promise).finally(() => {
          if (this.initialUrlRestorePromise === promise) this.initialUrlRestorePromise = null;
        });
      }
      async restoreUrlState({ completionFocus = false, focus = false, fromPopstate = false } = {}) {
        const restoreId = ++this.urlRestoreId;
        this.pendingAdvanceId += 1;
        this.pendingAdvanceFilter = '';
        this.setRouteLoading(false);
        this.cancelPendingInteractionFocus();
        if (fromPopstate && this.remoteMode) this.cancelRemoteResults();
        if (this.remoteMode && !this.routesLoaded) {
          this.pendingRouteUrlRestore = { completionFocus, focus, fromPopstate, restoreId };
          await this.ensureRoutesLoaded();
        }
        if (restoreId !== this.urlRestoreId || this.hfGiftFinderConnected === false) return false;
        if (this.completedUrlRestoreId === restoreId) return true;
        return this.applyUrlState({ completionFocus, focus, fromPopstate, restoreId });
      }
      applyUrlState({
        completionFocus = false,
        focus = false,
        fromPopstate = false,
        restoreId = this.urlRestoreId
      } = {}) {
        if (restoreId !== this.urlRestoreId || this.hfGiftFinderConnected === false) return false;
        if (this.remoteMode && this.routesLoadFailed) {
          this.pendingRouteUrlRestore = { completionFocus, focus, fromPopstate, restoreId };
          this.apply();
          return false;
        }

        const url = new URL(window.location.href);
        this.filters.forEach((filter) => this.clearSelection(filter));
        const restored = {};
        let validPrefixLength = 0;
        for (const filter of this.filters) {
          const key = this.urlKeyForFilter(filter);
          const values = url.searchParams.getAll(key);
          if (values.length !== 1) break;
          const option = this.optionForUrlValue(filter, values[0]);
          if (!option) break;
          const value = this.canonicalValue(filter, option.value || '');
          if (!this.optionAvailable(filter, value)) break;
          this.setSelectionFromOption(filter, option);
          restored[key] = value || 'any';
          validPrefixLength += 1;
        }

        const requestedStepValues = url.searchParams.getAll('gf_step');
        const requestedStep = requestedStepValues.length === 1 ? requestedStepValues[0] : '';
        const requestedFilter = this.filterForUrlStep(requestedStep);
        const allAnswered = validPrefixLength === this.filters.length;
        let activeFilter = validPrefixLength < this.filters.length
          ? this.filters[validPrefixLength]
          : null;
        if (requestedStep === 'results' && allAnswered) {
          activeFilter = null;
        } else if (requestedFilter) {
          const requestedIndex = this.filters.indexOf(requestedFilter);
          if (requestedIndex <= validPrefixLength) activeFilter = requestedFilter;
        }
        restored.gf_step = activeFilter ? this.urlStepForFilter(activeFilter) : 'results';

        this.restoringUrlState = true;
        this.activeFilter = activeFilter;
        this.resetLimit();
        if (!activeFilter && allAnswered && (focus || completionFocus)) {
          if (focus && !this.contains(document.activeElement)) {
            this.focus({ preventScroll: true });
          }
          this.queueCompletionFocus(true, document.activeElement);
        }
        this.apply();
        this.restoringUrlState = false;
        this.bindOrSettleCompletionFocus();
        if (focus && activeFilter) this.focusActiveControl();
        this.replaceGiftFinderUrlState(restored);
        if (!fromPopstate) this.initialUrlRestoreComplete = true;
        this.completedUrlRestoreId = restoreId;
        return true;
      }
      finishRouteLoad() {
        this.routesLoaded = true;
        if (this.routesLoadFailed) {
          this.apply();
          return;
        }
        this.pruneAfter(-1);
        const pending = this.pendingRouteUrlRestore;
        this.pendingRouteUrlRestore = null;
        if (
          pending
          && pending.restoreId === this.urlRestoreId
          && this.hfGiftFinderConnected !== false
        ) {
          this.applyUrlState(pending);
          return;
        }
        this.apply();
      }
      interactionFocusOwner(event, control) {
        if (
          event?.isTrusted === false
          || !control
          || document.activeElement !== control
          || !this.contains(control)
        ) return null;
        return control;
      }
      interactionFocusDocument() {
        if (this.ownerDocument) return this.ownerDocument;
        return typeof document === 'undefined' ? null : document;
      }
      neutralInteractionFocus(target, focusDocument = this.interactionFocusDocument()) {
        return !target
          || target === focusDocument?.body
          || target === focusDocument?.documentElement;
      }
      watchInteractionFocus(transaction) {
        this.clearInteractionFocusWatcher();
        const focusDocument = this.interactionFocusDocument();
        if (!transaction || !focusDocument?.addEventListener) return false;
        this.boundInteractionIntent = this.boundInteractionIntent || ((event) => {
          const pending = this.pendingInteractionFocusTransaction;
          if (
            !pending
            || (event.type === 'focusin' && event.target === pending.focusOwner)
          ) return;
          this.cancelPendingInteractionFocus();
        });
        this.pendingInteractionFocusDocument = focusDocument;
        this.pendingInteractionFocusTransaction = transaction;
        for (const type of ['focusin', 'pointerdown', 'keydown']) {
          focusDocument.addEventListener(type, this.boundInteractionIntent, true);
        }
        return true;
      }
      clearInteractionFocusWatcher(transaction = null) {
        if (transaction && this.pendingInteractionFocusTransaction !== transaction) return;
        for (const type of ['focusin', 'pointerdown', 'keydown']) {
          this.pendingInteractionFocusDocument?.removeEventListener?.(
            type,
            this.boundInteractionIntent,
            true
          );
        }
        this.pendingInteractionFocusDocument = null;
        this.pendingInteractionFocusTransaction = null;
      }
      interactionFocusStillOwned(transaction) {
        if (!transaction || this.pendingInteractionFocusTransaction !== transaction) return false;
        const activeElement = this.interactionFocusDocument()?.activeElement;
        return activeElement === transaction.focusOwner || this.neutralInteractionFocus(activeElement);
      }
      cancelPendingInteractionFocus() {
        this.clearInteractionFocusWatcher();
        this.pendingCompletionFocusId = Number(this.pendingCompletionFocusId || 0) + 1;
        this.pendingCompletionFocus = null;
        this.pendingLoadMoreFocusId = Number(this.pendingLoadMoreFocusId || 0) + 1;
        this.pendingLoadMoreFocus = null;
      }
      queueCompletionFocus(shouldFocus = true, focusOwner = null) {
        this.cancelPendingInteractionFocus();
        if (
          !shouldFocus
          || !focusOwner
          || document.activeElement !== focusOwner
          || !this.contains(focusOwner)
        ) return null;
        this.pendingCompletionFocus = {
          id: this.pendingCompletionFocusId,
          focusOwner,
          requestId: null
        };
        this.watchInteractionFocus(this.pendingCompletionFocus);
        return this.pendingCompletionFocus;
      }
      bindOrSettleCompletionFocus() {
        const pending = this.pendingCompletionFocus;
        if (!pending || pending.id !== this.pendingCompletionFocusId) return false;
        if (!this.interactionFocusStillOwned(pending)) {
          this.cancelPendingInteractionFocus();
          return false;
        }
        if (this.remoteMode && this.remoteLoading) {
          pending.requestId = this.remoteRequestId;
          return false;
        }
        this.pendingCompletionFocus = null;
        this.clearInteractionFocusWatcher(pending);
        return this.focusResults();
      }
      settleCompletionFocus(requestId) {
        const pending = this.pendingCompletionFocus;
        if (!pending || pending.id !== this.pendingCompletionFocusId) return false;
        if (pending.requestId !== null && pending.requestId !== requestId) return false;
        if (!this.interactionFocusStillOwned(pending)) {
          this.cancelPendingInteractionFocus();
          return false;
        }
        this.pendingCompletionFocus = null;
        this.clearInteractionFocusWatcher(pending);
        return this.focusResults();
      }
      stableProductIdentity(item) {
        if (!item) return '';
        const link = item.querySelector('a[href]');
        const href = link?.getAttribute('href');
        if (href) {
          const rawHref = String(href).trim();
          try {
            const baseUrl = new URL(window.location.href);
            const productUrl = new URL(rawHref, baseUrl);
            const pathname = productUrl.pathname.replace(/\/+$/, '') || '/';
            return productUrl.origin === baseUrl.origin
              ? `path:${pathname}`
              : `url:${productUrl.origin}${pathname}`;
          } catch (_error) {
            const stableHref = rawHref.split(/[?#]/, 1)[0].replace(/\/+$/, '');
            return `href:${stableHref || rawHref}`;
          }
        }
        const card = item.querySelector('[data-gf-product-card]') || item.querySelector('.hf-gf__card');
        const productKey = card?.dataset.gfProductKey
          || card?.dataset.productId
          || card?.dataset.productHandle
          || card?.dataset.title
          || '';
        return productKey ? `product:${productKey}` : '';
      }
      visibleProductEntries() {
        if (!this.grid) return [];
        return Array.from(this.grid.querySelectorAll('.hf-gf__item')).flatMap((item) => {
          if (item.hidden || !item.getClientRects().length) return [];
          const identity = this.stableProductIdentity(item);
          const link = item.querySelector('a[href]') || item.querySelector('[data-gf-product-card]');
          return identity && link && link.getClientRects().length ? [{ identity, link }] : [];
        });
      }
      beginLoadMoreFocusTransfer(event = null) {
        if (
          event?.isTrusted === false
          || !this.loadMore
          || document.activeElement !== this.loadMore
        ) return null;
        this.cancelPendingInteractionFocus();
        const transaction = {
          id: ++this.pendingLoadMoreFocusId,
          focusOwner: this.loadMore,
          requestId: null,
          visibleIdentities: new Set(this.visibleProductEntries().map(({ identity }) => identity))
        };
        this.pendingLoadMoreFocus = transaction;
        this.watchInteractionFocus(transaction);
        return transaction;
      }
      cancelLoadMoreFocusTransfer() {
        const pending = this.pendingLoadMoreFocus;
        this.pendingLoadMoreFocusId = Number(this.pendingLoadMoreFocusId || 0) + 1;
        this.pendingLoadMoreFocus = null;
        if (pending) this.clearInteractionFocusWatcher(pending);
      }
      loadMoreIsUsable() {
        return Boolean(
          this.loadMore
          && !this.loadMore.hidden
          && !this.loadMore.disabled
          && this.loadMore.getClientRects().length
        );
      }
      settleLoadMoreFocus(transaction, requestId = null) {
        if (
          !transaction
          || this.pendingLoadMoreFocus !== transaction
          || transaction.id !== this.pendingLoadMoreFocusId
          || (transaction.requestId !== null && transaction.requestId !== requestId)
        ) return false;
        if (!this.interactionFocusStillOwned(transaction)) {
          this.cancelLoadMoreFocusTransfer();
          return false;
        }
        this.pendingLoadMoreFocus = null;
        this.clearInteractionFocusWatcher(transaction);
        if (this.loadMoreIsUsable()) {
          this.loadMore.focus({ preventScroll: true });
          return document.activeElement === this.loadMore;
        }
        const newlyVisible = this.visibleProductEntries().find(
          ({ identity }) => !transaction.visibleIdentities.has(identity)
        );
        if (newlyVisible) {
          newlyVisible.link.focus({ preventScroll: true });
          if (document.activeElement === newlyVisible.link) {
            newlyVisible.link.scrollIntoView({ block: 'nearest', behavior: this.scrollBehavior() });
            return true;
          }
        }
        return this.focusResults();
      }
      createRecord(card, index) {
        return {
          card,
          item: card.closest('.hf-gf__item'),
          index,
          recipients: this.splitData(card.dataset.recipients),
          occasions: this.splitData(card.dataset.occasions),
          giftTypes: this.splitData(card.dataset.giftTypes),
          budgetTags: this.splitData(card.dataset.budgetTags),
          priceCents: Number(card.dataset.priceCents || 0),
          priority: Number(card.dataset.priority || 50)
        };
      }
      normalize(value) {
        return String(value || '').toLowerCase().replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
      }
      canonicalValue(filter, value) {
        const normalized = this.normalize(value);
        if (filter === 'recipient' && normalized === 'pet lover') return 'pet';
        return normalized;
      }
      splitData(value) {
        return this.normalize(value).split(',').map((item) => item.trim()).filter(Boolean);
      }
      ensureExtraTypes() {
        const select = this.querySelector('select[data-filter="giftType"]');
        const panel = this.querySelector('[data-gf-panel][data-filter="giftType"]');
        if (!select) return;
        [
          ['wine & prosecco', 'Wine & prosecco'],
          ['kitchen & dining', 'Kitchen & dining'],
          ['sweet treats', 'Sweet treats']
        ].forEach(([value, label]) => {
          if (!Array.from(select.options).some((option) => this.normalize(option.value) === value)) {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = label;
            select.appendChild(option);
          }
          if (panel && !Array.from(panel.querySelectorAll('[data-gf-option]')).some((option) => this.normalize(option.dataset.value) === value)) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'hf-gf__choice';
            button.dataset.gfOption = '';
            button.dataset.filter = 'giftType';
            button.dataset.value = value;
            button.setAttribute('aria-pressed', 'false');
            button.textContent = label;
            panel.appendChild(button);
          }
        });
      }
      giftTypeTagRules() {
        return [
          ['wine & prosecco', ['gf_type_alcohol']],
          ['sweet treats', ['gf_type_confectionery']],
          ['mugs', ['gf_type_mug']],
          ['trinket boxes', ['gf_type_trinket_box']],
          ['frames', ['gf_type_frame']],
          ['cards', ['gf_type_card']],
          ['lights', ['gf_type_light']],
          ['candles', ['gf_type_candle']],
          ['jewellery', ['gf_type_jewellery', 'gf_type_watch']],
          ['drinkware', ['gf_type_drinkware']],
          ['kitchen & dining', ['gf_type_kitchen_dining', 'gf_type_mealtime']],
          ['stationery', ['gf_type_stationery', 'gf_type_book']],
          ['plush', ['gf_type_plush']],
          [
            'bags & textiles',
            [
              'gf_type_bag_textile',
              'gf_type_clothing',
              'gf_type_pet_bandana',
              'gf_type_pet_bowl_mat',
              'gf_nav_christmas_stockings_sacks'
            ]
          ],
          [
            'home decor',
            [
              'gf_type_home_decor',
              'gf_type_christmas_decoration',
              'gf_type_garden_tool',
              'gf_type_pet_bowl',
              'gf_type_pet_lead_hook',
              'gf_type_pet_storage',
              'gf_type_pet_treat_jar',
              'gf_type_ornament',
              'gf_type_storage'
            ]
          ],
          [
            'keepsakes',
            [
              'gf_type_keepsake',
              'gf_type_compact_mirror',
              'gf_type_memorial_urn',
              'gf_type_money_box',
              'gf_type_toy'
            ]
          ]
        ];
      }
      primaryGiftTypeForTags(tags) {
        const accepted = tags && typeof tags.has === 'function' ? tags : new Set(this.splitData(tags));
        const match = this.giftTypeTagRules().find(([, acceptedTags]) => acceptedTags.some((tag) => accepted.has(tag)));
        return match ? match[0] : '';
      }
      initialLimit() {
        const mobile = window.matchMedia('(width < 750px)').matches;
        const fallback = mobile ? 4 : 6;
        const value = Number(mobile ? this.dataset.initialMobile : this.dataset.initialDesktop);
        return value > 0 ? value : fallback;
      }
      loadIncrement() {
        const value = Number(this.dataset.loadIncrement);
        return value > 0 ? value : 4;
      }
      lowResultThreshold() {
        return 3;
      }
      cancelScheduledRouteLoad() {
        if (this.routePreloadHandler && this.routePreloadListenersAttached) {
          this.removeEventListener('focusin', this.routePreloadHandler);
          this.removeEventListener('pointerdown', this.routePreloadHandler);
        }
        this.routePreloadListenersAttached = false;
        if (this.routeObserver) this.routeObserver.disconnect();
        this.routeObserver = null;
      }
      scheduleRouteLoad() {
        if (this.routesLoaded || this.routesLoadPromise || this.routePreloadListenersAttached || this.routeObserver) return;
        const load = this.routePreloadHandler || (() => {
          this.cancelScheduledRouteLoad();
          this.ensureRoutesLoaded();
        });
        this.routePreloadHandler = load;
        this.routePreloadListenersAttached = true;
        this.addEventListener('focusin', load, { once: true });
        this.addEventListener('pointerdown', load, { once: true, passive: true });
        if (!('IntersectionObserver' in window)) {
          load();
          return;
        }
        this.routeObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.routeObserver.disconnect();
          this.routeObserver = null;
          load();
        }, { rootMargin: '400px 0px' });
        this.routeObserver.observe(this);
      }
      ensureRoutesLoaded() {
        if (!this.remoteMode || this.routesLoaded) return Promise.resolve();
        if (this.routesLoadPromise) return this.routesLoadPromise;
        this.cancelScheduledRouteLoad();
        this.routeAbortController?.abort();
        const controller = new AbortController();
        this.routeAbortController = controller;
        const routeLoadPromise = this.loadRoutes(controller.signal).finally(() => {
          if (this.routeAbortController === controller) this.routeAbortController = null;
          if (this.routesLoadPromise === routeLoadPromise) this.routesLoadPromise = null;
        });
        this.routesLoadPromise = routeLoadPromise;
        return routeLoadPromise;
      }
      retryRoutes() {
        if (this.routeLoading) return this.routesLoadPromise || Promise.resolve();
        this.routesLoaded = false;
        this.routesLoadFailed = false;
        this.setRouteLoading(true);
        const retry = this.ensureRoutesLoaded();
        this.apply();
        return retry.finally(() => {
          if (this.hfGiftFinderConnected === false || this.isConnected === false) return;
          this.setRouteLoading(false);
          this.apply();
        });
      }
      setCount(value) {
        if (!this.count) return;
        const count = Number(value);
        this.count.textContent = Number.isFinite(count) ? count : value;
        if (!this.countLabel || !Number.isFinite(count)) return;
        const singular = this.dataset.resultCountSingular || this.dataset.resultCountLabel || 'matching product';
        const plural = this.dataset.resultCountLabel || 'matching products';
        this.countLabel.textContent = count === 1 ? singular : plural;
      }
      announceStatus(message) {
        if (!this.liveStatus) return;
        const next = String(message || '');
        if (this.liveStatus.textContent === next) return;
        this.liveStatus.textContent = next;
      }
      announceResultProgress(visible, total) {
        const shown = Number(visible);
        const available = Number(total);
        if (!Number.isFinite(shown) || !Number.isFinite(available)) return;
        const singular = this.dataset.resultCountSingular || this.dataset.resultCountLabel || 'matching product';
        const plural = this.dataset.resultCountLabel || 'matching products';
        const label = available === 1 ? singular : plural;
        this.announceStatus(
          shown < available
            ? `Showing ${shown} of ${available} ${plural}`
            : `${available} ${label}`
        );
      }
      markAnySelection(filter) {
        const select = this.getSelect(filter);
        if (!select) return false;
        const anyOption = this.options.find((option) => option.dataset.filter === filter && (option.dataset.value || '') === '');
        const fallbackLabel = select.options[0] ? select.options[0].text : 'Any';
        select.value = '';
        select.dataset.answered = 'true';
        select.dataset.selectedLabel = anyOption ? anyOption.textContent.trim() : fallbackLabel;
        return true;
      }
      updateOptionNote() {
        if (!this.optionNote) return;
        if (!this.activeFilter || this.activeFilter === 'recipient') {
          this.optionNote.hidden = true;
          return;
        }
        const panel = this.panels.find((item) => item.dataset.filter === this.activeFilter);
        if (!panel || panel.hidden) {
          this.optionNote.hidden = true;
          return;
        }
        const hiddenOptions = Array.from(panel.querySelectorAll('[data-gf-option]'))
          .filter((option) => option.hidden && (option.dataset.value || '') !== '');
        this.optionNote.hidden = hiddenOptions.length === 0;
      }
      updateCompletionCopy(total) {
        if (this.activeFilter || !this.panelText) return;
        const count = Number(total);
        if (Number.isFinite(count) && count > 0 && count <= this.lowResultThreshold() && this.dataset.lowResultHelper) {
          this.panelText.textContent = this.dataset.lowResultHelper;
          return;
        }
        if (this.dataset.completeHelper) this.panelText.textContent = this.dataset.completeHelper;
      }
      fallbackRecord() {
        const configuredValues = (filter) => Array.from(this.getSelect(filter)?.options || [])
          .map((option) => this.canonicalValue(filter, option.value))
          .filter(Boolean);
        return {
          index: Number.MAX_SAFE_INTEGER,
          recipients: configuredValues('recipient'),
          occasions: configuredValues('occasion'),
          giftTypes: configuredValues('giftType'),
          budgetTags: configuredValues('budget'),
          recipientFallback: true,
          priceCents: 0,
          priority: 100,
          routeCount: 0
        };
      }
      failOpenRoutes(error = null) {
        this.records = [];
        this.routePrefixCounts = {};
        this.routeMinimums = {};
        this.routesLoadFailed = true;
        this.hfRouteIndexTrusted = false;
        this.hfStaticRouteCountsTrusted = false;
        this.hfRouteIndexCoverage = 0;
        this.hfRouteIndexIndexedProducts = 0;
        this.hfRouteIndexExpectedProducts = 0;
        this.hfRouteIndexSource = 'unavailable';
        if (error) console.warn('[Heartfelt Gifts] Gift Finder choices could not be loaded.', error);
      }
      sourceScopeMatches(metadata) {
        const renderedHandle = String(this.dataset?.sourceHandle || '').trim();
        const renderedTag = String(this.dataset?.sourceTag || '').trim();
        const manifestHandle = String(metadata?.collection || '').trim();
        const manifestTag = String(metadata?.sourceTag || '').trim();
        return Boolean(
          renderedHandle === 'personalised-gifts'
          && renderedTag === 'gf_personalised'
          && renderedHandle === manifestHandle
          && renderedTag === manifestTag
        );
      }
      activateReviewedRenderedSourceScope() {
        const renderedHandle = String(this.dataset?.sourceHandle || '').trim();
        const renderedTag = String(this.dataset?.sourceTag || '').trim();
        const valid = renderedHandle === 'personalised-gifts' && renderedTag === 'gf_personalised';
        this.hfSourceScopeValid = valid;
        this.hfValidatedSourceTag = valid ? renderedTag : '';
        return valid;
      }
      renderedSourceCount() {
        const value = String(this.dataset?.sourceProductCount ?? '').trim();
        if (!/^[1-9]\d*$/.test(value)) return null;
        const count = Number(value);
        return Number.isSafeInteger(count) ? count : null;
      }
      staticRouteCountsMatch(metadata) {
        const renderedCount = this.renderedSourceCount();
        return Boolean(
          renderedCount !== null
          && renderedCount === metadata?.catalogueProducts
          && renderedCount === metadata?.personalisedProducts
        );
      }
      routeManifestTrusted(payload, routes, prefixCounts) {
        const metadata = payload?.metadata;
        const catalogue = metadata?.catalogueProducts;
        const personalised = metadata?.personalisedProducts;
        const available = metadata?.availablePersonalisedProducts;
        const indexed = metadata?.indexedProducts;
        const coverage = metadata?.strictCoverage;
        const requiredIndexes = [
          'occasion',
          'recipientOccasion',
          'recipientGiftType',
          'recipientOccasionGiftType',
          'recipientOccasionGiftTypeBudget'
        ];
        return Boolean(
          payload?.version === 1
          && Array.isArray(payload.routes)
          && payload.routes.length > 0
          && routes.length === payload.routes.length
          && Number.isSafeInteger(catalogue)
          && catalogue > 0
          && Number.isSafeInteger(personalised)
          && personalised === catalogue
          && Number.isSafeInteger(available)
          && available > 0
          && indexed === available
          && coverage === 1
          && this.sourceScopeMatches(metadata)
          && /^[a-f0-9]{64}$/i.test(String(metadata?.sourceFingerprint || ''))
          && requiredIndexes.every((name) => (
            prefixCounts[name] instanceof Map
            && prefixCounts[name].size === payload.prefixCounts?.[name]?.length
          ))
        );
      }
      async loadRoutes(signal) {
        this.activateReviewedRenderedSourceScope();
        this.hfStaticRouteCountsTrusted = false;
        if (!this.dataset.routesUrl) {
          this.failOpenRoutes(new Error('Route manifest URL is missing.'));
          this.finishRouteLoad();
          return;
        }
        try {
          const response = await fetch(this.dataset.routesUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal
          });
          if (!response.ok) throw new Error('Route index unavailable');
          const payload = await response.json();
          if (signal?.aborted) return;
          this.hfSourceScopeValid = this.sourceScopeMatches(payload?.metadata);
          this.hfValidatedSourceTag = this.hfSourceScopeValid
            ? String(payload.metadata.sourceTag)
            : '';
          const routes = this.decodeRoutes(payload);
          const prefixCounts = this.decodePrefixCounts(payload);
          if (!this.routeManifestTrusted(payload, routes, prefixCounts)) {
            throw new Error('Route manifest integrity check failed.');
          }
          this.routePrefixCounts = prefixCounts;
          this.routeMinimums = payload?.minimums && typeof payload.minimums === 'object'
            ? payload.minimums
            : {};
          this.records = routes.map((route, index) => ({
            index,
            recipients: route.recipient ? [this.normalize(route.recipient)] : [],
            occasions: route.occasion ? [this.normalize(route.occasion)] : [],
            giftTypes: route.giftType ? [this.normalize(route.giftType)] : [],
            budgetTags: route.budget ? [this.normalize(route.budget)] : [],
            recipientFallback: route.recipientFallback === true,
            priceCents: 0,
            priority: 50,
            routeCount: Number(route.count || 1)
          }));
          this.routesLoadFailed = false;
          this.hfRouteIndexTrusted = true;
          this.hfStaticRouteCountsTrusted = this.staticRouteCountsMatch(payload.metadata);
          this.hfRouteIndexCoverage = Number(payload.metadata.strictCoverage);
          this.hfRouteIndexIndexedProducts = Number(payload.metadata.indexedProducts);
          this.hfRouteIndexExpectedProducts = Number(payload.metadata.availablePersonalisedProducts);
          this.hfRouteIndexSource = 'hf-gift-finder-routes.json';
          this.hfRouteMetadata = {
            ...payload.metadata,
            indexedPersonalisedProducts: Number(payload.metadata.indexedProducts),
            runtimeSource: this.hfRouteIndexSource,
            runtimeIntegrityGate: `${payload.metadata.availablePersonalisedProducts} indexed published in-stock products`
          };
        } catch (error) {
          if (signal?.aborted || error?.name === 'AbortError') return;
          this.failOpenRoutes(error);
        }
        if (signal?.aborted) return;
        this.finishRouteLoad();
      }
      decodeRoutes(payload) {
        if (Array.isArray(payload)) return payload;
        if (payload?.version !== 1 || !payload.dimensions || !Array.isArray(payload.routes)) return [];
        const dimensionNames = ['recipient', 'occasion', 'giftType', 'budget'];
        const dimensions = dimensionNames.map((name) => payload.dimensions[name]);
        if (dimensions.some((values) => !Array.isArray(values))) return [];
        return payload.routes.flatMap((row) => {
          if (!Array.isArray(row) || row.length < 5) return [];
          const values = dimensions.map((dimension, index) => dimension[row[index]]);
          const count = Number(row[4]);
          if (values.some((value) => typeof value !== 'string') || !Number.isFinite(count) || count < 1) return [];
          return [{
            recipient: values[0],
            occasion: values[1],
            giftType: values[2],
            budget: values[3],
            count,
            recipientFallback: row[5] === 1
          }];
        });
      }
      decodePrefixCounts(payload) {
        if (!payload?.dimensions || !payload.prefixCounts) return {};
        const definitions = {
          occasion: ['occasion'],
          recipientOccasion: ['recipient', 'occasion'],
          recipientGiftType: ['recipient', 'giftType'],
          recipientOccasionGiftType: ['recipient', 'occasion', 'giftType'],
          recipientOccasionGiftTypeBudget: ['recipient', 'occasion', 'giftType', 'budget']
        };
        return Object.entries(definitions).reduce((indexes, [name, dimensions]) => {
          const rows = payload.prefixCounts[name];
          if (!Array.isArray(rows) || dimensions.some((dimension) => !Array.isArray(payload.dimensions[dimension]))) {
            return indexes;
          }
          const index = new Map();
          rows.forEach((row) => {
            if (!Array.isArray(row) || row.length !== dimensions.length + 1) return;
            const values = dimensions.map((dimension, position) => payload.dimensions[dimension][row[position]]);
            const count = Number(row[row.length - 1]);
            if (values.some((value) => typeof value !== 'string') || !Number.isFinite(count) || count < 1) return;
            index.set(values.map((value) => this.normalize(value)).join('\u001f'), count);
          });
          indexes[name] = index;
          return indexes;
        }, {});
      }
      tagMap() {
        return {
          recipient: {
            mum: 'gf_recipient_mum',
            dad: 'gf_recipient_dad',
            grandad: 'gf_recipient_grandad',
            grandmother: 'gf_recipient_grandmother',
            her: 'gf_recipient_her',
            him: 'gf_recipient_him',
            couple: 'gf_recipient_couple',
            child: 'gf_recipient_child',
            pet: 'gf_recipient_pet',
            'pet lover': 'gf_recipient_pet',
            teacher: 'gf_recipient_teacher',
            'in memory': 'gf_recipient_in_memory'
          },
          occasion: {
            birthday: 'gf_occasion_birthday',
            wedding: 'gf_occasion_wedding',
            anniversary: 'gf_occasion_anniversary',
            memorial: 'gf_occasion_memorial',
            'new baby': 'gf_occasion_new_baby',
            'baby shower': 'gf_occasion_baby_shower',
            baptism: 'gf_occasion_baptism',
            christening: 'gf_occasion_christening',
            confirmation: 'gf_occasion_confirmation',
            'first holy communion': 'gf_occasion_holy_communion',
            graduation: 'gf_occasion_graduation',
            easter: 'gf_occasion_easter',
            halloween: 'gf_occasion_halloween',
            'hen party': 'gf_occasion_hen_party',
            christmas: 'gf_occasion_christmas',
            'thank you': 'gf_occasion_thank_you',
            "mother's day": 'gf_occasion_mothers_day',
            "father's day": 'gf_occasion_fathers_day',
            "valentine's day": 'gf_occasion_valentines',
            housewarming: 'gf_occasion_housewarming'
          },
          giftType: Object.fromEntries(
            this.giftTypeTagRules().map(([value, tags]) => [value, tags[0]])
          )
        };
      }
      tagFor(filter, value) {
        return this.tagMap()[filter]?.[this.normalize(value)] || '';
      }
      tagAliases() {
        return {
          recipient: {
            child: ['gf_recipient_baby'],
            her: ['gf_recipient_bride', 'gf_recipient_bridesmaid'],
            him: ['gf_recipient_best_man', 'gf_recipient_groom', 'gf_recipient_groomsman', 'gf_recipient_husband'],
            couple: [],
            grandmother: ['gf_recipient_nan']
          },
          giftType: Object.fromEntries(
            this.giftTypeTagRules().map(([value, tags]) => [value, tags.slice(1)])
          )
        };
      }
      tagsFor(filter, value) {
        const normalized = this.normalize(value);
        const canonical = this.tagFor(filter, normalized);
        const aliases = this.tagAliases()[filter]?.[normalized] || [];
        return [...new Set([canonical, ...aliases].filter(Boolean))];
      }
      compatibilityTags(filter, value) {
        const normalized = this.normalize(value);
        if (filter === 'recipient') {
          if (['mum', 'dad', 'grandad', 'grandmother', 'her', 'him'].includes(normalized)) {
            return ['gf_audience_adult'];
          }
          if (normalized === 'couple') return ['gf_audience_couple'];
          if (normalized === 'child') return ['gf_audience_child'];
          if (normalized === 'teacher') return ['gf_suitable_teacher'];
          return [];
        }
        if (filter === 'occasion') {
          const canonical = this.tagFor('occasion', normalized);
          return canonical ? [canonical.replace('gf_occasion_', 'gf_suitable_')] : [];
        }
        return [];
      }
      matchingTags(filter, value) {
        return [...new Set([
          ...this.tagsFor(filter, value),
          ...this.compatibilityTags(filter, value)
        ])];
      }
      compatibilityMarkerSlug(value) {
        return this.normalize(value)
          .replace(/[’']/g, '')
          .replace(/&/g, ' and ')
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
      }
      compatibilityMarkerPresent(tags, filter, value) {
        const markerTags = tags instanceof Set ? tags : new Set(tags || []);
        const dimension = filter === 'recipient' ? 'recipient' : 'occasion';
        const taxonomyPrefix = `gf_${dimension}_`;
        const slugs = [...new Set([
          this.compatibilityMarkerSlug(value),
          ...this.tagsFor(filter, value).map((tag) => (
            tag.startsWith(taxonomyPrefix) ? tag.slice(taxonomyPrefix.length) : ''
          ))
        ].filter(Boolean))];
        return markerTags.has(`gf_compat_${dimension}_all`)
          || slugs.some((slug) => markerTags.has(`gf_compat_${dimension}_${slug}`));
      }
      budgetParams(value) {
        const budget = this.normalize(value);
        const numbers = budget.match(/\d+/g)?.map((number) => Number(number)) || [];
        const params = [];
        const exclusiveUpperBound = (number) => Math.max(0, number - 0.01).toFixed(2);
        if (!budget || numbers.length === 0) return params;
        if (budget.includes('under')) {
          params.push(['filter.v.price.lte', exclusiveUpperBound(numbers[0])]);
        } else if (budget.includes('+')) {
          params.push(['filter.v.price.gte', String(numbers[0])]);
        } else if (numbers.length >= 2) {
          params.push(['filter.v.price.gte', String(numbers[0])]);
          params.push(['filter.v.price.lte', exclusiveUpperBound(numbers[1])]);
        }
        return params;
      }
      searchTagClause(tags) {
        const safeTags = [...new Set((Array.isArray(tags) ? tags : []).filter(
          (tag) => /^gf_[a-z0-9_]+$/.test(String(tag || ''))
        ))];
        if (safeTags.length === 0) return '';
        const terms = safeTags.map((tag) => `tag:${tag}`);
        return terms.length === 1 ? terms[0] : `(${terms.join(' OR ')})`;
      }
      validatedSourceTag() {
        const sourceTag = String(
          this.hfValidatedSourceTag || this.hfRouteMetadata?.sourceTag || ''
        ).trim();
        if (this.hfRouteMetadata && !this.sourceScopeMatches(this.hfRouteMetadata)) return '';
        return /^gf_[a-z0-9_]+$/.test(sourceTag) ? sourceTag : '';
      }
      remoteSearchQuery(values) {
        const sourceTag = this.validatedSourceTag();
        const clauses = [
          sourceTag ? `tag:${sourceTag}` : '',
          '-tag:gf_finder_exclude',
          '-tag:gf_taxonomy_review'
        ].filter(Boolean);
        const recipientValue = values.recipient?.answered ? values.recipient.value : '';
        if (recipientValue) {
          const recipientClause = this.searchTagClause(this.matchingTags('recipient', recipientValue));
          if (recipientClause) clauses.push(recipientClause);
        }
        const occasionValue = values.occasion?.answered ? values.occasion.value : '';
        if (occasionValue) {
          const occasionClause = this.searchTagClause(this.matchingTags('occasion', occasionValue));
          if (occasionClause) clauses.push(occasionClause);
        }
        const giftValue = values.giftType?.answered ? this.normalize(values.giftType.value) : '';
        if (giftValue) {
          const rules = this.giftTypeTagRules();
          const selectedIndex = rules.findIndex(([value]) => value === giftValue);
          const giftClause = this.searchTagClause(selectedIndex >= 0 ? rules[selectedIndex][1] : []);
          if (giftClause) clauses.push(giftClause);
          const higherPrecedenceTags = selectedIndex > 0
            ? rules.slice(0, selectedIndex).flatMap(([, tags]) => tags)
            : [];
          const exclusionClause = this.searchTagClause(higherPrecedenceTags);
          if (exclusionClause) clauses.push(`-${exclusionClause}`);
        }
        return clauses.join(' AND ');
      }
      remoteUrls(values, page) {
        if (this.hfSourceScopeValid !== true || !this.validatedSourceTag()) return [];
        const searchPath = this.dataset.searchUrl || '/search';
        const url = new URL(searchPath, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);
        ['_ab', '_fd', '_sc', 'preview_theme_id'].forEach((key) => {
          const value = currentParams.get(key);
          if (value !== null && !url.searchParams.has(key)) url.searchParams.set(key, value);
        });
        url.searchParams.set('q', this.remoteSearchQuery(values));
        url.searchParams.set('sort_by', 'price-ascending');
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[unavailable_products]', 'hide');
        url.searchParams.set('options[prefix]', 'none');
        url.searchParams.set('section_id', 'hf-gift-finder-results');
        if (page > 1) url.searchParams.set('page', String(page));
        // Native price facets broaden Shopify search results; budget is checked locally.
        return [url.toString()];
      }
      remoteUrl(values, page) {
        return this.remoteUrls(values, page)[0] || '';
      }
      remoteCollectionUrl(values, page) {
        if (this.hfSourceScopeValid !== true || !this.validatedSourceTag()) return '';
        const source = new URL(this.dataset.sourceUrl || '', window.location.origin);
        const handle = this.dataset.sourceHandle;
        if (source.origin !== window.location.origin || source.pathname.replace(/\/$/, '') !== `/collections/${handle}`) return '';
        source.pathname = source.pathname.replace(/\/$/, '');
        const giftType = this.selectedValue(values, 'giftType');
        const tags = this.giftTypeTagRules().find(([value]) => value === giftType)?.[1];
        if (tags?.length === 1 && /^gf_[a-z0-9_]+$/.test(tags[0])) source.pathname += `/${tags[0]}`;
        source.search = '';
        const currentParams = new URLSearchParams(window.location.search);
        ['_ab', '_fd', '_sc', 'preview_theme_id'].forEach((key) => {
          const value = currentParams.get(key);
          if (value !== null) source.searchParams.set(key, value);
        });
        source.searchParams.set('section_id', 'hf-gift-finder-results');
        source.searchParams.set('sort_by', 'price-ascending');
        if (page > 1) source.searchParams.set('page', String(page));
        if (values.budget?.answered) {
          this.budgetParams(values.budget.value).forEach(([key, value]) => source.searchParams.set(key, value));
        }
        return source.toString();
      }
      validateRemotePage(grid, pageInfo, page) {
        const data = pageInfo?.dataset;
        const total = Number(data?.total);
        const expectedSize = Math.max(0, Math.min(250, total - (page - 1) * 250));
        if (!/^\d+$/.test(data?.total || '') || !Number.isSafeInteger(total)
          || !/^[1-9]\d*$/.test(data?.page || '') || Number(data.page) !== page
          || !['true', 'false'].includes(data?.hasNextPage)
          || grid?.children.length !== expectedSize) {
          throw new Error('Gift finder result page coverage unavailable');
        }
      }
      async requestCompleteRemoteResults(values, signal) {
        const checkAbort = () => {
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        };
        checkAbort();
        let collection = false;
        let first = await this.requestRemotePage(values, 1, signal, collection);
        checkAbort();
        if (Number(first.pageInfo.dataset.total) >= 1000) {
          collection = true;
          first = await this.requestRemotePage(values, 1, signal, collection);
          checkAbort();
        }
        const total = Number(first.pageInfo.dataset.total);
        const pageCount = Math.max(1, Math.ceil(total / 250));
        if (!Number.isSafeInteger(total) || total < 0 || pageCount > 100) throw new Error('Gift finder result coverage unavailable');
        const newGrid = document.createElement('ul');
        const seen = new Set();
        const rawSeen = new Set();
        const retain = (result, page) => {
          checkAbort();
          this.validateRemotePage(result.newGrid, result.pageInfo, page);
          if (Number(result.pageInfo.dataset.total) !== total || (result.pageInfo.dataset.hasNextPage === 'true') !== (page < pageCount)) {
            throw new Error('Gift finder results changed; please try again');
          }
          Array.from(result.newGrid.children).forEach((item) => {
            const key = this.stableProductIdentity(item);
            if (!key || rawSeen.has(key)) throw new Error('Gift finder unique result coverage unavailable');
            rawSeen.add(key);
            if (seen.has(key) || !this.remoteItemMatches(item, values)) return;
            seen.add(key);
            newGrid.appendChild(item);
          });
        };
        retain(first, 1);
        // Bound network and detached DOM usage; preserve source order for ranking.
        for (let page = 2; page <= pageCount; page += 3) {
          checkAbort();
          const pages = Array.from({ length: Math.min(3, pageCount - page + 1) }, (_, index) => page + index);
          const results = await Promise.all(pages.map((number) => this.requestRemotePage(values, number, signal, collection)));
          results.forEach((result, index) => retain(result, pages[index]));
        }
        checkAbort();
        if (rawSeen.size !== total) throw new Error('Gift finder result coverage incomplete');
        return { newGrid, pageInfo: { dataset: { total: String(newGrid.children.length), page: '1', hasNextPage: 'false' } } };
      }
      syncBusyState() {
        const busy = Boolean(this.routeLoading || this.remoteLoading);
        this.setAttribute('aria-busy', busy ? 'true' : 'false');
        if (this.grid) this.grid.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
      setRouteLoading(loading) {
        this.routeLoading = loading;
        this.classList.toggle('hf-gf--routes-pending', loading);
        this.syncBusyState();
        this.updateRouteStatus();
        if (this.next && loading) this.next.disabled = true;
      }
      updateRouteStatus() {
        if (!this.routeStatus) return;
        this.routeStatus.hidden = !this.routeLoading && !this.routesLoadFailed;
        this.routeStatus.textContent = this.routeLoading
          ? (this.dataset.loadingLabel || 'Loading')
          : this.routesLoadFailed ? 'Gift choices could not be loaded. Please try again.' : '';
      }
      setLoading(loading) {
        this.remoteLoading = loading;
        this.classList.toggle('hf-gf--loading', loading);
        this.syncBusyState();
        if (this.loadMore) this.loadMore.disabled = loading;
        if (loading && this.dataset.loadingLabel) this.announceStatus(this.dataset.loadingLabel);
      }
      remoteItems() {
        return this.grid ? Array.from(this.grid.querySelectorAll('.hf-gf__item')) : [];
      }
      remoteItemCard(item) {
        return item?.querySelector('[data-gf-product-card]');
      }
      containsAny(value, phrases) {
        return phrases.some((phrase) => value.includes(phrase));
      }
      selectedValue(values, filter) {
        return values[filter]?.answered ? this.normalize(values[filter].value) : '';
      }
      remoteBudgetMatches(item, value) {
        if (!value) return true;
        const card = this.remoteItemCard(item);
        const priceCents = Number(card?.dataset.priceCents || 0);
        const range = this.budgetRange(value);
        if (!priceCents || !range) return false;
        const price = priceCents / 100;
        return price >= range.min && price < range.max;
      }
      remoteDimensionMatches(item, filter, value) {
        const selected = this.normalize(value);
        if (!selected) return true;
        const card = this.remoteItemCard(item);
        const tags = this.splitData(card?.dataset.tags);
        if (filter === 'giftType') {
          return this.primaryGiftTypeForTags(new Set(tags)) === selected;
        }
        const acceptedTags = this.matchingTags(filter, selected);
        return acceptedTags.length > 0 && acceptedTags.some((tag) => tags.includes(tag));
      }
      remoteItemMatches(item, values) {
        const card = this.remoteItemCard(item);
        const tags = new Set(this.splitData(card?.dataset.tags));
        if (!tags.has(this.validatedSourceTag()) || card?.dataset.available !== 'true') return false;
        if (tags.has('gf_finder_exclude') || tags.has('gf_taxonomy_review')) return false;
        if (!this.remoteDimensionMatches(item, 'recipient', this.selectedValue(values, 'recipient'))) return false;
        if (!this.remoteDimensionMatches(item, 'occasion', this.selectedValue(values, 'occasion'))) return false;
        if (!this.remoteDimensionMatches(item, 'giftType', this.selectedValue(values, 'giftType'))) return false;
        return !values.budget?.answered || this.remoteBudgetMatches(item, values.budget.value);
      }
      scoreRemoteItem(item, values, index) {
        const card = this.remoteItemCard(item);
        if (!card) return 0 - index;
        const title = this.normalize(card.dataset.title || card.textContent || '');
        const tags = this.normalize(card.dataset.tags || '');
        const tagSet = new Set(this.splitData(card.dataset.tags || ''));
        const haystack = `${title} ${tags}`;
        const recipient = this.selectedValue(values, 'recipient');
        const occasion = this.selectedValue(values, 'occasion');
        const giftType = this.selectedValue(values, 'giftType');
        let score = 1000 - index;
        const exactDimensionMatches = {};
        [
          ['recipient', recipient, 90],
          ['occasion', occasion, 76],
          ['giftType', giftType, 70]
        ].forEach(([filter, value, weight]) => {
          if (!value) return;
          const exactTagMatch = this.tagsFor(filter, value).some((tag) => tagSet.has(tag));
          exactDimensionMatches[filter] = exactTagMatch;
          if (exactTagMatch) score += weight;
          if (exactTagMatch && haystack.includes(value)) score += Math.round(weight * 0.35);
        });
        if (values.budget?.answered && this.remoteBudgetMatches(item, values.budget.value)) score += 24;
        if (giftType === 'frames') {
          if (this.containsAny(haystack, ['frame', 'photo frame', 'framed'])) score += 34;
          if (recipient === 'couple' && occasion === 'wedding') {
            if (this.containsAny(haystack, ['wedding frame', 'wedding photo frame', 'our wedding', 'mr & mrs', 'mr and mrs', 'bride and groom', 'couple'])) score += 78;
            if (this.containsAny(haystack, ['mother of the bride', 'father of the bride', 'mother of the groom', 'father of the groom', 'parents of the bride', 'parents of the groom'])) score -= 86;
          }
        }
        if (giftType === 'mugs' && this.containsAny(haystack, ['mug', 'cup'])) score += 34;
        if (giftType === 'keepsakes' && this.containsAny(haystack, ['keepsake', 'memory box', 'memorial', 'token'])) score += 34;
        if (recipient === 'pet' && exactDimensionMatches.recipient) {
          if (this.containsAny(haystack, ['pet', 'dog', 'cat', 'paw', 'pet bowl', 'pet mat'])) score += 64;
          if (this.containsAny(haystack, ['dog tag necklace', 'necklace'])) score -= 42;
        }
        if (recipient === 'teacher' && exactDimensionMatches.recipient && this.containsAny(haystack, ['teacher', 'school', 'classroom', 'thank you teacher'])) score += 70;
        if (recipient === 'mum' && exactDimensionMatches.recipient && this.containsAny(haystack, ['mum', 'mummy', 'mother', 'mama'])) score += 58;
        if (recipient === 'dad' && exactDimensionMatches.recipient && this.containsAny(haystack, ['dad', 'daddy', 'father'])) score += 58;
        if (recipient === 'grandad' && this.containsAny(haystack, ['grandad', 'granddad', 'grandpa', 'grandfather'])) score += 58;
        const hasGrandmotherTerm = this.containsAny(haystack, ['nanna', 'granny', 'grandma', 'grandmother'])
          || /(?:^|[^a-z])(?:nan|nana|gran)(?:[^a-z]|$)/.test(haystack);
        if (recipient === 'grandmother' && hasGrandmotherTerm) score += 58;
        if (((recipient === 'in memory' && exactDimensionMatches.recipient) || (occasion === 'memorial' && exactDimensionMatches.occasion)) && this.containsAny(haystack, ['memorial', 'remembrance', 'in memory', 'keepsake', 'forever'])) score += 78;
        if (occasion === 'birthday' && exactDimensionMatches.occasion && this.containsAny(haystack, ['birthday', 'age ', 'years old'])) score += 54;
        if (occasion === 'wedding' && exactDimensionMatches.occasion && this.containsAny(haystack, ['wedding', 'bride', 'groom', 'mr & mrs', 'mr and mrs'])) score += 54;
        if (occasion === 'anniversary' && exactDimensionMatches.occasion && this.containsAny(haystack, ['anniversary', 'years together'])) score += 54;
        const recipientDowngraded = Boolean(
          recipient
          && exactDimensionMatches.recipient
          && this.compatibilityMarkerPresent(tagSet, 'recipient', recipient)
        );
        const occasionDowngraded = Boolean(
          occasion
          && exactDimensionMatches.occasion
          && this.compatibilityMarkerPresent(tagSet, 'occasion', occasion)
        );
        if (recipientDowngraded) score -= 196;
        if (occasionDowngraded) score -= 160;
        if (exactDimensionMatches.recipient && exactDimensionMatches.occasion) {
          if (recipientDowngraded && occasionDowngraded) score -= 64;
          else if (recipientDowngraded || occasionDowngraded) score -= 46;
        }
        return score;
      }
      sortRemoteGrid(values) {
        if (!this.grid) return [];
        const items = this.remoteItems();
        items.forEach((item) => { item.hidden = true; });
        const ranked = items
          .filter((item) => this.remoteItemMatches(item, values))
          .map((item, index) => ({ item, index, score: this.scoreRemoteItem(item, values, index) }))
          .sort((a, b) => b.score - a.score || a.index - b.index);
        ranked.forEach(({ item }, index) => {
          item.style.order = index;
          this.grid.appendChild(item);
        });
        return ranked.map(({ item }) => item);
      }
      exactStaticRouteCount(values) {
        const filters = Array.isArray(this.filters) && this.filters.length > 0
          ? this.filters
          : ['recipient', 'occasion', 'giftType', 'budget'];
        if (!filters.every((filter) => values?.[filter]?.answered === true)) return null;
        const index = this.routePrefixCounts?.recipientOccasionGiftTypeBudget;
        if (!index || typeof index.has !== 'function' || typeof index.get !== 'function') return null;
        const key = filters.map((filter) => this.normalize(values[filter].value)).join('\u001f');
        if (!index.has(key)) return null;
        const count = Number(index.get(key));
        return Number.isSafeInteger(count) && count > 0 ? count : null;
      }
      reconcileLiveRouteCount(values, liveTotal) {
        if (!this.hfStaticRouteCountsTrusted) return;
        const expected = this.exactStaticRouteCount(values);
        const actual = Number(liveTotal);
        if (expected !== null && Number.isSafeInteger(actual) && actual >= 0 && actual !== expected) {
          this.hfStaticRouteCountsTrusted = false;
        }
      }
      applyRemoteDisplay(values) {
        if (!this.grid) return;
        const items = this.sortRemoteGrid(values);
        items.forEach((item, index) => {
          item.hidden = index >= this.visibleLimit;
        });
        const hiddenLocalResults = items.some((item) => item.hidden);
        const total = this.remoteTotal || items.length;
        this.reconcileLiveRouteCount(values, total);
        this.setCount(total);
        this.updateCompletionCopy(total);
        if (this.empty) this.empty.classList.toggle('is-visible', this.remoteError || (items.length === 0 && !this.remoteHasNext));
        if (this.loadMore) this.loadMore.hidden = this.remoteLoading || (!hiddenLocalResults && !this.remoteHasNext);
        const visible = items.filter((item) => !item.hidden).length;
        return { visible, total };
      }
      applyRemoteCardPresentation(root) {
        const showMeta = this.dataset.showCardMeta === 'true';
        const fromPriceLabel = String(this.dataset.fromPriceLabel || '').trim();
        const cardCtaLabel = String(this.dataset.cardCtaLabel || '').trim();
        const cards = Array.from(root?.querySelectorAll?.('[data-gf-product-card]') || []);
        cards.forEach((card) => {
          const meta = card.querySelector?.('[data-gf-card-meta]');
          const description = card.querySelector?.('[data-gf-card-description]');
          const pricePrefix = card.querySelector?.('[data-gf-card-price-prefix]');
          const cta = card.querySelector?.('[data-gf-card-cta]');
          if (meta) meta.hidden = !(showMeta && Boolean(meta.textContent.trim()));
          if (description) description.hidden = !Boolean(description.textContent.trim());
          if (pricePrefix) {
            const showPricePrefix = card.dataset.priceVaries === 'true' && fromPriceLabel !== '';
            pricePrefix.textContent = showPricePrefix ? fromPriceLabel : '';
            pricePrefix.hidden = !showPricePrefix;
          }
          if (cta) {
            cta.textContent = cardCtaLabel;
            cta.hidden = cardCtaLabel === '';
          }
        });
      }
      async requestRemotePage(values, page, signal, collection = false) {
        const url = collection ? this.remoteCollectionUrl(values, page) : this.remoteUrl(values, page);
        if (!url) throw new Error('Gift finder results unavailable');
        const response = await fetch(url, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error('Gift finder results unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fragment = doc.querySelector('[data-gf-results-fragment]') || doc;
        const grid = fragment.querySelector('[data-gf-remote-grid]');
        if (!grid) throw new Error('Gift finder results missing');
        const resultPageInfo = fragment.querySelector('[data-gf-page-info]');
        this.validateRemotePage(grid, resultPageInfo, page);
        const newGrid = document.createElement('ul');
        newGrid.className = grid.className || 'hf-gf__grid';
        newGrid.setAttribute('role', 'list');
        newGrid.dataset.gfRemoteGrid = '';
        Array.from(grid.children).forEach((item) => newGrid.appendChild(item.cloneNode(true)));
        const pageInfo = document.createElement('span');
        pageInfo.dataset.total = String(
          Number(resultPageInfo.dataset.total)
        );
        pageInfo.dataset.page = resultPageInfo.dataset.page;
        pageInfo.dataset.hasNextPage = resultPageInfo?.dataset.hasNextPage === 'true' ? 'true' : 'false';
        pageInfo.hidden = true;
        return { newGrid, pageInfo };
      }
      async fetchRemoteResults(values, reset = true, focusTransaction = null) {
        if (!this.remoteMode || (!reset && this.remoteLoading)) return;
        if (!focusTransaction && this.pendingLoadMoreFocus) this.cancelLoadMoreFocusTransfer();
        const page = reset ? 1 : this.remotePage + 1;
        const requestId = ++this.remoteRequestId;
        if (focusTransaction && this.pendingLoadMoreFocus === focusTransaction) {
          focusTransaction.requestId = requestId;
        }
        this.remoteAbortController?.abort();
        const controller = new AbortController();
        this.remoteAbortController = controller;
        this.setLoading(true);
        this.remoteError = false;
        if (this.empty) {
          this.empty.textContent = this.emptyDefaultText;
          this.empty.classList.remove('is-visible');
        }
        try {
          const { newGrid, pageInfo } = await this.requestCompleteRemoteResults(values, controller.signal);
          if (requestId !== this.remoteRequestId) return;
          if (!this.grid) throw new Error('Gift finder results missing');
          if (reset) this.grid.innerHTML = newGrid.innerHTML;
          else Array.from(newGrid.children).forEach((item) => this.grid.appendChild(item));
          this.applyRemoteCardPresentation(this.grid);
          this.remoteHasNext = pageInfo?.dataset.hasNextPage === 'true';

          this.remotePage = page;
          this.remoteTotal = Number(pageInfo?.dataset.total || this.grid.children.length || 0);
          this.setLoading(false);
          const display = this.applyRemoteDisplay(values);
          this.announceResultProgress(display.visible, display.total);
          this.settleCompletionFocus(requestId);
          this.settleLoadMoreFocus(focusTransaction, requestId);
        } catch (error) {
          if (requestId !== this.remoteRequestId) return;
          if (controller.signal.aborted || error?.name === 'AbortError') return;
          this.remoteError = true;
          if (!reset && this.grid && this.grid.children.length > 0) {
            this.remoteHasNext = false;
            this.setLoading(false);
            this.applyRemoteDisplay(values);
            if (this.empty) {
              this.empty.textContent = this.dataset.errorLabel || this.emptyDefaultText;
              this.empty.classList.add('is-visible');
            }
            this.settleCompletionFocus(requestId);
            this.settleLoadMoreFocus(focusTransaction, requestId);
            return;
          }
          this.setCount(0);
          if (this.empty) {
            this.empty.textContent = this.dataset.errorLabel || this.emptyDefaultText;
            this.empty.classList.add('is-visible');
          }
          if (this.loadMore) this.loadMore.hidden = true;
          this.settleCompletionFocus(requestId);
          this.settleLoadMoreFocus(focusTransaction, requestId);
        } finally {
          if (this.remoteAbortController === controller) this.remoteAbortController = null;
          if (requestId === this.remoteRequestId) this.setLoading(false);
        }
      }
      cancelRemoteResults() {
        this.remoteRequestId += 1;
        this.remoteAbortController?.abort();
        this.remoteAbortController = null;
        this.remoteError = false;
        this.remoteHasNext = false;
        this.remoteTotal = 0;
        this.setLoading(false);
      }
      resetLimit() {
        this.visibleLimit = this.initialLimit();
      }
      scrollBehavior() {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }
      focusResults() {
        const target = this.resultsFocus || this.querySelector('.hf-gf__item:not([hidden]) .hf-gf__card');
        if (!target || !target.getClientRects().length) return false;
        target.focus({ preventScroll: true });
        if (document.activeElement !== target) return false;
        target.scrollIntoView({ block: 'nearest', behavior: this.scrollBehavior() });
        return true;
      }
      focusActiveControl() {
        if (!this.activeFilter) return this.focusResults();
        const panel = this.panels.find((item) => item.dataset.filter === this.activeFilter);
        const option = panel?.querySelector(
          '[data-gf-option][aria-pressed="true"]:not([hidden]):not([disabled]), [data-gf-option]:not([hidden]):not([disabled])'
        );
        if (option) {
          option.focus();
          return true;
        }
        const select = this.getSelect(this.activeFilter);
        if (select) {
          select.focus();
          return true;
        }
        return false;
      }
      activateFilter(filter, {
        completionFocus = false,
        completionFocusOwner = null,
        focus = false,
        serialize = true
      } = {}) {
        if (filter) {
          this.activeFilter = filter;
          this.apply();
          if (focus) this.focusActiveControl();
        } else {
          this.activeFilter = null;
          this.queueCompletionFocus(completionFocus, completionFocusOwner);
          this.apply();
          this.bindOrSettleCompletionFocus();
        }
        if (serialize) this.serializeUrlState();
        return true;
      }
      advanceFromFilter(filter, { focusOwner = null } = {}) {
        if (!filter) return false;
        if (this.remoteMode && this.routesLoadFailed) {
          this.apply();
          return false;
        }
        if (!this.isAnswered(filter)) {
          if (filter === 'giftType' && !this.remoteMode) this.markAnySelection('giftType');
          else return false;
        }
        if (this.remoteMode && !this.routesLoaded) {
          const pendingId = ++this.pendingAdvanceId;
          this.pendingAdvanceFilter = filter;
          this.setRouteLoading(true);
          this.apply();
          this.ensureRoutesLoaded()
            .then(() => {
              if (this.hfGiftFinderConnected === false || this.isConnected === false) return;
              if (pendingId !== this.pendingAdvanceId || this.pendingAdvanceFilter !== filter) return;
              this.pendingAdvanceFilter = '';
              this.setRouteLoading(false);
              this.advanceFromFilter(filter, { focusOwner });
            })
            .catch(() => {
              if (pendingId !== this.pendingAdvanceId) return;
              this.pendingAdvanceFilter = '';
              this.setRouteLoading(false);
            });
          return true;
        }
        const index = this.filters.indexOf(filter);
        if (index < 0) return false;
        const nextFilter = this.filters[index + 1] || null;
        return this.activateFilter(nextFilter, {
          completionFocus: !nextFilter,
          completionFocusOwner: !nextFilter ? focusOwner : null,
          focus: Boolean(nextFilter)
        });
      }
      getSelect(filter) {
        return this.selects.find((s) => s.dataset.filter === filter);
      }
      clearSelection(filter) {
        const select = this.getSelect(filter);
        if (!select) return;
        select.value = '';
        delete select.dataset.answered;
        delete select.dataset.selectedLabel;
      }
      isAnswered(filter) {
        const select = this.getSelect(filter);
        return Boolean(select && (select.value || select.dataset.answered === 'true'));
      }
      selectionLabel(filter) {
        const select = this.getSelect(filter);
        if (!select || !this.isAnswered(filter)) return '';
        if (select.dataset.selectedLabel) return select.dataset.selectedLabel;
        return select.options[select.selectedIndex] ? select.options[select.selectedIndex].text : '';
      }
      stepLabel(filter) {
        const step = this.steps.find((item) => item.dataset.filter === filter);
        const stepLabel = step?.querySelector('.hf-gf__step-label')?.textContent?.trim();
        if (stepLabel) return stepLabel;
        const select = this.getSelect(filter);
        return select?.closest('.hf-gf__field')?.querySelector('label')?.textContent?.trim() || filter;
      }
      currentSelection() {
        return this.filters.reduce((values, filter) => {
          const select = this.getSelect(filter);
          values[filter] = {
            answered: this.isAnswered(filter),
            value: this.canonicalValue(filter, select ? select.value : '')
          };
          return values;
        }, {});
      }
      selectionForOption(filter, value) {
        const filterIndex = this.filters.indexOf(filter);
        return this.filters.reduce((values, current, index) => {
          if (index < filterIndex && this.isAnswered(current)) {
            const select = this.getSelect(current);
            values[current] = {
              answered: true,
              value: this.canonicalValue(current, select ? select.value : '')
            };
          }
          if (current === filter) {
            values[current] = {
              answered: true,
              value: this.canonicalValue(current, value)
            };
          }
          return values;
        }, {});
      }
      canAccessStep(index) {
        if (index <= 0) return true;
        return this.filters.slice(0, index).every((filter) => this.isAnswered(filter));
      }
      pruneAfter(index) {
        let clearRest = false;
        this.filters.forEach((filter, filterIndex) => {
          if (filterIndex <= index) return;
          if (clearRest) {
            this.clearSelection(filter);
            return;
          }
          if (!this.isAnswered(filter)) return;
          const select = this.getSelect(filter);
          if (!this.optionAvailable(filter, select ? select.value : '')) {
            this.clearSelection(filter);
            clearRest = true;
          }
        });
      }
      matchesList(list, value) {
        return !value || list.includes(value);
      }
      budgetRange(value) {
        const budget = this.normalize(value);
        const numbers = budget.match(/\d+/g)?.map((number) => Number(number)) || [];
        if (!budget || numbers.length === 0) return null;
        if (budget.includes('under')) return { min: 0, max: numbers[0] };
        if (budget.includes('+')) return { min: numbers[0], max: Infinity };
        if (numbers.length >= 2) return { min: numbers[0], max: numbers[1] };
        return null;
      }
      budgetTagsMatch(tags, value) {
        if (tags.length === 0 || tags.includes(value)) return true;
        const selected = this.budgetRange(value);
        if (!selected) return false;
        return tags.some((tag) => {
          const candidate = this.budgetRange(tag);
          if (!candidate) return false;
          return candidate.min >= selected.min && candidate.max <= selected.max;
        });
      }
      budgetMatches(record, value) {
        if (!value) return true;
        if (record.priceCents > 0) {
          const numbers = value.match(/\d+/g)?.map((number) => Number(number)) || [];
          if (value.includes('under') && numbers.length >= 1) return record.priceCents < numbers[0] * 100;
          if (value.includes('+') && numbers.length >= 1) return record.priceCents >= numbers[0] * 100;
          if (numbers.length >= 2) return record.priceCents >= numbers[0] * 100 && record.priceCents < numbers[1] * 100;
        }
        return this.budgetTagsMatch(record.budgetTags, value);
      }
      recordMatches(record, values) {
        if (this.selectionIncompatible(values)) return false;
        const recipient = values.recipient;
        const occasion = values.occasion;
        const giftType = values.giftType;
        const budget = values.budget;
        if (recipient?.answered && recipient.value && !this.matchesList(record.recipients, recipient.value)) return false;
        if (occasion?.answered && occasion.value && !this.matchesList(record.occasions, occasion.value)) return false;
        if (giftType?.answered && giftType.value && !this.matchesList(record.giftTypes, giftType.value)) return false;
        if (budget?.answered && budget.value && !this.budgetMatches(record, budget.value)) return false;
        return true;
      }
      recordExactMatches(record, values) {
        if (!this.recordMatches(record, values)) return false;
        return ['recipient', 'occasion', 'giftType'].every((filter) => {
          const selection = values[filter];
          if (!selection?.answered || !selection.value) return true;
          if (filter === 'recipient' && record.recipientFallback) return false;
          const listName = filter === 'giftType' ? 'giftTypes' : `${filter}s`;
          return record[listName].includes(selection.value);
        });
      }
      selectionIncompatible(values) {
        const recipient = values.recipient?.answered ? this.normalize(values.recipient.value) : '';
        const occasion = values.occasion?.answered ? this.normalize(values.occasion.value) : '';
        if (occasion === 'hen party' && recipient !== 'her') return true;
        if (!recipient || !occasion) return false;
        const blockedOccasions = {
          mum: ["father's day"],
          her: ["father's day"],
          dad: ["mother's day"],
          him: ["mother's day"],
          grandad: ["mother's day"],
          grandmother: ["father's day"],
          child: ["mother's day", "father's day", "valentine's day"],
          teacher: ["mother's day", "father's day"]
        };
        return blockedOccasions[recipient]?.includes(occasion) || false;
      }
      hasProducts(values) {
        if (this.selectionIncompatible(values)) return false;
        if (this.remoteMode && (!this.routesLoaded || this.routesLoadFailed || this.records.length === 0)) return false;
        return this.records.some((record) => this.recordMatches(record, values));
      }
      minimumProductsForOption(filter, value) {
        if (!this.normalize(value)) return 1;
        const datasetKey = `minimum${filter.charAt(0).toUpperCase()}${filter.slice(1)}Products`;
        const configured = Number(this.dataset[datasetKey]);
        if (Number.isFinite(configured) && configured > 0) return configured;
        const indexedMinimum = Number(this.routeMinimums[filter]);
        if (Number.isFinite(indexedMinimum) && indexedMinimum > 0) return indexedMinimum;
        return filter === 'occasion' ? 3 : 1;
      }
      prefixCountForOption(filter, value) {
        const selection = this.selectionForOption(filter, value);
        const selected = (name) => selection[name]?.answered ? this.normalize(selection[name].value) : '';
        const recipient = selected('recipient');
        const occasion = selected('occasion');
        const giftType = selected('giftType');
        const budget = selected('budget');
        let indexName = '';
        let keyValues = [];
        if (filter === 'occasion' && occasion) {
          indexName = recipient ? 'recipientOccasion' : 'occasion';
          keyValues = recipient ? [recipient, occasion] : [occasion];
        } else if (filter === 'giftType' && recipient && giftType) {
          indexName = occasion ? 'recipientOccasionGiftType' : 'recipientGiftType';
          keyValues = occasion ? [recipient, occasion, giftType] : [recipient, giftType];
        } else if (filter === 'budget' && recipient && occasion && giftType && budget) {
          indexName = 'recipientOccasionGiftTypeBudget';
          keyValues = [recipient, occasion, giftType, budget];
        }
        if (!indexName) return null;
        const index = this.routePrefixCounts[indexName];
        if (!(index instanceof Map)) return null;
        return Number(index.get(keyValues.join('\u001f')) || 0);
      }
      optionAvailable(filter, value) {
        if (this.remoteMode && !this.routesLoaded) return filter === 'recipient';
        const selection = this.selectionForOption(filter, value);
        if (this.selectionIncompatible(selection)) return false;
        return this.hasProducts(selection);
      }
      syncChoices() {
        this.options.forEach((option) => {
          const filter = option.dataset.filter;
          const value = option.dataset.value || '';
          const available = this.optionAvailable(filter, value);
          option.hidden = !available;
          option.disabled = !available;
        });
        this.selects.forEach((select) => {
          Array.from(select.options).forEach((option) => {
            const available = this.optionAvailable(select.dataset.filter, option.value || '');
            option.hidden = !available;
            option.disabled = !available;
          });
        });
      }
      setActivePanel(filter) {
        this.panels.forEach((panel) => {
          const active = Boolean(filter && panel.dataset.filter === filter);
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        const values = this.currentSelection();
        const memorialGiftType = filter === 'giftType' && (
          this.selectedValue(values, 'recipient') === 'in memory' || this.selectedValue(values, 'occasion') === 'memorial'
        );
        const questionKey = memorialGiftType
          ? 'questionMemorialGiftType'
          : filter === 'giftType' ? 'questionGiftType' : filter ? `question${filter.charAt(0).toUpperCase()}${filter.slice(1)}` : '';
        const helperKey = memorialGiftType
          ? 'helperMemorialGiftType'
          : filter === 'giftType' ? 'helperGiftType' : filter ? `helper${filter.charAt(0).toUpperCase()}${filter.slice(1)}` : '';
        const title = questionKey ? this.dataset[questionKey] : this.dataset.completeTitle;
        const helper = helperKey ? this.dataset[helperKey] : this.dataset.completeHelper;
        if (this.panelTitle && title) this.panelTitle.textContent = title;
        if (this.panelText && helper) this.panelText.textContent = helper;
        if (!this.question) return;
        const activeSelect = filter ? this.getSelect(filter) : null;
        const label = activeSelect?.closest('.hf-gf__field')?.querySelector('label')?.textContent?.trim();
        if (filter === 'recipient') {
          this.question.hidden = false;
          this.question.textContent = this.question.dataset.initialText || this.question.textContent;
        } else if (label) {
          this.question.hidden = false;
          this.question.textContent = label;
        } else {
          this.question.hidden = true;
        }
      }
      updateActions(isComplete) {
        const activeIndex = this.activeFilter ? this.filters.indexOf(this.activeFilter) : this.filters.length;
        if (this.back) {
          const showBack = activeIndex > 0;
          this.back.hidden = !showBack;
          if (showBack) {
            const previousLabel = this.stepLabel(this.filters[activeIndex - 1]).toLowerCase();
            this.back.textContent = `${this.dataset.backLabel || 'Back'} to ${previousLabel}`;
          }
        }
        if (!this.next) return;
        if (this.remoteMode && this.routesLoadFailed) {
          this.next.hidden = false;
          this.next.disabled = this.routeLoading;
          this.next.textContent = 'Retry loading choices';
          return;
        }
        if (!this.activeFilter && isComplete) {
          this.next.hidden = true;
          return;
        }
        if (!this.activeFilter) {
          this.next.hidden = true;
          return;
        }
        const answered = this.isAnswered(this.activeFilter);
        const nextFilter = this.filters[activeIndex + 1];
        const canSkip = this.activeFilter === 'giftType' && !this.remoteMode;
        this.next.hidden = false;
        this.next.disabled = !answered && !canSkip;
        if (!answered && canSkip && nextFilter) {
          this.next.textContent = `${this.dataset.nextPrefix || 'Next: choose'} ${this.stepLabel(nextFilter).toLowerCase()}`;
        } else if (!answered) {
          this.next.textContent = `Choose ${this.stepLabel(this.activeFilter).toLowerCase()}`;
        } else if (nextFilter) {
          this.next.textContent = `${this.dataset.nextPrefix || 'Next: choose'} ${this.stepLabel(nextFilter).toLowerCase()}`;
        } else {
          this.next.textContent = this.dataset.finalLabel || 'Show matching gifts';
        }
        if (this.routeLoading) this.next.disabled = true;
      }
      scoreRecord(record, values) {
        let score = 0;
        if (values.recipient?.value && record.recipients.includes(values.recipient.value)) score += 16;
        if (values.occasion?.value && record.occasions.includes(values.occasion.value)) score += 12;
        if (values.giftType?.value && record.giftTypes.includes(values.giftType.value)) score += 10;
        if (values.budget?.value && this.budgetMatches(record, values.budget.value)) score += 6;
        return score * 100 - record.priority;
      }
      apply() {
        if (this.activeFilter === 'budget' && !this.isAnswered('budget')) {
          this.markAnySelection('budget');
        }
        const values = this.currentSelection();
        const chosen = this.filters.map((filter) => this.selectionLabel(filter)).filter(Boolean);
        const hasSelection = chosen.length > 0;
        const stepValues = this.filters.map((filter) => this.isAnswered(filter));
        const isComplete = stepValues.every(Boolean);
        const firstIncomplete = stepValues.findIndex((value) => !value);
        if (this.activeFilter && !this.canAccessStep(this.filters.indexOf(this.activeFilter))) {
          this.activeFilter = firstIncomplete >= 0 ? this.filters[firstIncomplete] : null;
        } else if (!this.activeFilter && !isComplete) {
          this.activeFilter = firstIncomplete >= 0 ? this.filters[firstIncomplete] : this.filters[0];
        }
        const activeStep = this.activeFilter ? this.filters.indexOf(this.activeFilter) : this.filters.length;
        const requireComplete = this.classList.contains('hf-gf--require-complete');
        const revealResults = requireComplete ? isComplete && !this.activeFilter : true;
        const waiting = (requireComplete && !revealResults) || (this.classList.contains('hf-gf--hide-unselected') && !hasSelection);
        const routesPending = this.remoteMode && !this.routesLoaded && (!waiting || this.routeLoading);
        const incompatible = this.remoteMode && this.selectionIncompatible(values);
        this.classList.toggle('hf-gf--has-selection', hasSelection);
        this.classList.toggle('hf-gf--results-ready', !waiting && !routesPending);
        this.classList.toggle('hf-gf--routes-pending', routesPending);
        this.syncChoices();
        this.updateRouteStatus();
        this.setActivePanel(this.activeFilter);
        this.updateOptionNote();
        this.steps.forEach((step, index) => {
          const filter = step.dataset.filter;
          const button = step.querySelector('[data-gf-step]');
          const active = index === activeStep;
          step.classList.toggle('is-active', active);
          step.classList.toggle('is-complete', Boolean(stepValues[index]));
          step.classList.toggle('is-accessible', this.canAccessStep(index));
          if (button) {
            button.disabled = !this.canAccessStep(index);
            if (active) button.setAttribute('aria-current', 'step');
            else button.removeAttribute('aria-current');
            const state = button.querySelector('[data-gf-step-state]');
            if (state) {
              state.textContent = stepValues[index] && !active
                ? ` - ${this.dataset.completedStepLabel || 'Completed'}`
                : '';
            }
          }
        });
        const signature = this.filters.map((filter) => `${filter}:${values[filter]?.answered ? values[filter].value : '-'}`).join('|');
        const signatureChanged = signature !== this.lastSignature;
        if (signatureChanged) {
          this.lastSignature = signature;
          this.resetLimit();
        }
        const matches = this.records
          .filter((record) => this.recordMatches(record, values))
          .sort((a, b) => this.scoreRecord(b, values) - this.scoreRecord(a, values) || a.index - b.index);
        if (this.remoteMode) {
          if (routesPending) this.ensureRoutesLoaded();
          if (waiting || routesPending || incompatible || this.routesLoadFailed) {
            if (this.remoteLoading) this.cancelRemoteResults();
            if (this.grid) this.grid.innerHTML = '';
            this.remoteHasNext = false;
            this.remoteTotal = 0;
            if (routesPending && this.dataset.loadingLabel) {
              this.setCount(this.dataset.loadingLabel);
              if (this.countLabel) this.countLabel.textContent = '';
            } else {
              this.setCount(0);
            }
            if (this.loadMore) this.loadMore.hidden = true;
            if (this.empty) {
              this.empty.textContent = this.emptyDefaultText;
              this.empty.classList.toggle('is-visible', incompatible && !waiting && !routesPending);
            }
          } else {
            if (signatureChanged || (this.grid && this.grid.children.length === 0 && !this.remoteLoading)) {
              this.fetchRemoteResults(values, true);
            } else {
              this.applyRemoteDisplay(values);
            }
            if (this.empty) this.empty.classList.toggle('is-visible', this.remoteError);
          }
          if (this.selected) {
            this.selected.hidden = waiting || !hasSelection;
            this.selected.textContent = hasSelection ? this.dataset.selectedLabel + ' ' + chosen.join(' / ') : '';
          }
          this.options.forEach((option) => {
            const select = this.getSelect(option.dataset.filter);
            const value = option.dataset.value || '';
            const active = Boolean(
              select &&
              this.isAnswered(option.dataset.filter) &&
              this.normalize(select.value) === this.normalize(value) &&
              (value || select.dataset.selectedLabel === option.textContent.trim())
            );
            option.classList.toggle('is-selected', active);
            option.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          this.updateActions(isComplete);
          return;
        }
        this.records.forEach((record) => {
          if (record.item) record.item.hidden = true;
          else record.card.hidden = true;
        });
        matches.forEach((record, index) => {
          const show = !waiting && index < this.visibleLimit;
          if (record.item) {
            record.item.hidden = !show;
            record.item.style.order = index;
          } else {
            record.card.hidden = !show;
          }
        });
        if (this.empty) this.empty.classList.toggle('is-visible', !waiting && matches.length === 0);
        this.setCount(waiting ? 0 : matches.length);
        if (!waiting) this.updateCompletionCopy(matches.length);
        if (this.loadMore) this.loadMore.hidden = waiting || matches.length <= this.visibleLimit;
        if (this.selected) {
          this.selected.hidden = waiting || !hasSelection;
          this.selected.textContent = hasSelection ? this.dataset.selectedLabel + ' ' + chosen.join(' / ') : '';
        }
        this.options.forEach((option) => {
          const select = this.getSelect(option.dataset.filter);
          const value = option.dataset.value || '';
          const active = Boolean(
            select &&
            this.isAnswered(option.dataset.filter) &&
            select.value === value &&
            (value || select.dataset.selectedLabel === option.textContent.trim())
          );
          option.classList.toggle('is-selected', active);
          option.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this.updateActions(isComplete);
      }
    }
    Object.defineProperties(HeartfeltGiftFinder.prototype, {
      hfFullCoveragePatched: { value: true },
      hfTaxonomyGiftTypesPatched: { value: true },
      hfCoverageMetadataPatched: { value: true }
    });
    customElements.define('gift-finder', HeartfeltGiftFinder);
  }
